import { useToast } from "@/hooks/use-toast";
import { useEffect, useRef, useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Copy, Eye, Loader2, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CityAiAnalysisDetail, CityAiAnalysisListResponse, CityAiAnalysisRefreshStatus, CityAiAnalysisRow, RefreshAllStartResponse } from "./types";
import { formatDate, formatUsd } from "./helpers";

// Task #1147 — Admin panel surfacing the city_ai_analyses cache so staff
// can verify the monthly Gemini refresh cron is healthy and re-trigger
// individual cities (or the whole batch) on demand.
export function CityAiAnalysesPanel({ canManage = true }: { canManage?: boolean } = {}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // 2026-04-29 — Tracks which row's full markdown is open in the
  // preview dialog. `null` = closed. We key by row id so the
  // preview query below can fetch on demand and React Query can
  // cache per-row.
  const [previewRow, setPreviewRow] = useState<CityAiAnalysisRow | null>(null);
  // 2026-04-29 — Tracks which row's Copy button is currently
  // fetching, so we can show a per-row spinner without blocking
  // other Copy clicks.
  const [copyingRowId, setCopyingRowId] = useState<number | null>(null);

  // Task #1156 — refreshStatus is part of the list response. While a
  // batch is in flight we poll every 3s so the table picks up newly
  // landed rows and the progress indicator stays live; when the
  // runner is idle we fall back to a 60s refetch (cheap enough that
  // we don't bother turning it off entirely).
  const listQuery = useQuery<CityAiAnalysisListResponse>({
    queryKey: ["/api/admin/city-ai-analyses"],
    queryFn: async () => {
      const res = await fetch("/api/admin/city-ai-analyses", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load city analyses");
      return res.json();
    },
    refetchInterval: (query) => {
      const status = query.state.data?.refreshStatus;
      return status?.state === "running" ? 3_000 : 60_000;
    },
  });

  const refreshStatus = listQuery.data?.refreshStatus;
  const isBatchRunning = refreshStatus?.state === "running";

  // Task #1156 — When the runner transitions from "running" to
  // "completed"/"failed" we surface a one-shot toast with the same
  // totals the pre-#1156 inline-await response used to show. We track
  // the previous state via a ref so the effect only fires on the
  // edge, not on every poll while idle.
  const prevStateRef = useRef<CityAiAnalysisRefreshStatus["state"] | null>(null);
  useEffect(() => {
    const currentState = refreshStatus?.state ?? null;
    const prevState = prevStateRef.current;
    if (
      prevState === "running" &&
      (currentState === "completed" || currentState === "failed") &&
      refreshStatus
    ) {
      if (currentState === "completed") {
        const costSummary = ` Gemini spend: ${formatUsd(refreshStatus.totalCostUsd)} (${refreshStatus.totalInputTokens.toLocaleString()} in / ${refreshStatus.totalOutputTokens.toLocaleString()} out tokens).`;
        toast({
          title: "Batch refresh complete",
          description: `Attempted ${refreshStatus.attempted}, refreshed ${refreshStatus.refreshed}, skipped ${refreshStatus.skipped}, failed ${refreshStatus.failed}.${costSummary}`,
        });
      } else {
        toast({
          title: "Batch refresh failed",
          description:
            refreshStatus.lastError ??
            "The background refresh crashed before it could finish.",
          variant: "destructive",
        });
      }
    }
    prevStateRef.current = currentState;
  }, [refreshStatus, toast]);

  const refreshOneMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (input: { city: string; state: string }) => {
      const res = await apiRequest("POST", "/api/admin/city-ai-analyses/refresh-one", input);
      return res.json() as Promise<CityAiAnalysisRow>;
    },
    onSuccess: (row) => {
      // Task #1148 — Surface Gemini spend in the toast so an admin
      // who clicks "Refresh now" sees immediately how much that one
      // refresh cost (vs. having to switch to the Gemini Usage tab).
      const tokenSummary =
        row.inputTokens != null && row.outputTokens != null
          ? ` ${row.inputTokens.toLocaleString()} in / ${row.outputTokens.toLocaleString()} out tokens`
          : "";
      const costSummary = row.costUsd != null ? ` — ${formatUsd(row.costUsd)}` : "";
      toast({
        title: "Refreshed",
        description: `${row.city}, ${row.state} regenerated (${row.byteSize.toLocaleString()} bytes).${tokenSummary}${costSummary}`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/city-ai-analyses"] });
    },
    onError: (err: unknown) => {
      toast({
        title: "Refresh failed",
        description: parseErrorMessage(err, "Could not refresh this city."),
        variant: "destructive",
      });
    },
  });

  // Task #1156 — Refresh-all now kicks off a background batch; the
  // mutation returns ~immediately with `{ started, status }`. The
  // success toast just confirms the batch was enqueued (or that one
  // was already running) — the per-completion totals toast fires
  // separately from the polling effect above when the runner reaches
  // a terminal state.
  const refreshAllMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/city-ai-analyses/refresh-all", {});
      return res.json() as Promise<RefreshAllStartResponse>;
    },
    onSuccess: (result) => {
      if (result.started) {
        toast({
          title: "Batch refresh started",
          description: `Refreshing ${result.status.attempted || "all"} cities in the background. The table will update as cities finish.`,
        });
      } else {
        toast({
          title: "Refresh already running",
          description: `A refresh is already in progress (${result.status.completed} of ${result.status.attempted || "?"} cities done). Watch the progress bar.`,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/admin/city-ai-analyses"] });
    },
    onError: (err: unknown) => {
      toast({
        title: "Batch refresh failed to start",
        description: parseErrorMessage(err, "Could not start the batch refresh."),
        variant: "destructive",
      });
    },
  });

  const rows = listQuery.data?.rows ?? [];
  const staleCount = listQuery.data?.staleCount ?? 0;
  const staleDays = listQuery.data?.staleDaysThreshold ?? 35;
  const totals = listQuery.data?.totals;
  const refreshingKey = refreshOneMutation.isPending
    ? `${refreshOneMutation.variables?.city}|${refreshOneMutation.variables?.state}`
    : null;

  // 2026-04-29 — Lazy fetch the full markdown for the row currently
  // open in the preview dialog. React Query caches per-row id, so
  // re-opening the same row shows instantly. Cache stays for 5 min
  // since the only thing that invalidates it is a refresh of that
  // city, which already invalidates the list query above.
  const previewQuery = useQuery<CityAiAnalysisDetail>({
    queryKey: ["/api/admin/city-ai-analyses", previewRow?.id],
    queryFn: async () => {
      const res = await fetch(`/api/admin/city-ai-analyses/${previewRow!.id}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load city analysis");
      return res.json();
    },
    enabled: previewRow !== null,
    staleTime: 5 * 60 * 1000,
  });

  // 2026-04-29 — Fetches the full markdown for a row and writes it
  // to the system clipboard. Used by both the per-row Copy button
  // and the Copy button inside the preview dialog (when we already
  // have the cached detail in hand we skip the fetch).
  async function copyMarkdownForRow(row: CityAiAnalysisRow, prefetched?: string) {
    // Capability precheck — `navigator.clipboard.writeText` is only
    // exposed in secure contexts (HTTPS or localhost) and behind a
    // user-gesture in some embedded iframe contexts. Bail with a clear
    // message instead of the generic browser error and skip the
    // unnecessary detail fetch when copy isn't even possible.
    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
      toast({
        title: "Copy not available",
        description:
          "Clipboard access requires a secure (HTTPS) context. Open the admin panel in a top-level browser tab and try again.",
        variant: "destructive",
      });
      return;
    }
    setCopyingRowId(row.id);
    try {
      let analysis = prefetched;
      if (!analysis) {
        const res = await fetch(`/api/admin/city-ai-analyses/${row.id}`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Failed to load city analysis");
        const data: CityAiAnalysisDetail = await res.json();
        analysis = data.analysis;
      }
      await navigator.clipboard.writeText(analysis);
      toast({
        title: "Copied to clipboard",
        description: `${row.city}, ${row.state} — ${analysis.length.toLocaleString()} characters of markdown copied.`,
      });
    } catch (err) {
      toast({
        title: "Copy failed",
        description: parseErrorMessage(err, "Could not copy the markdown."),
        variant: "destructive",
      });
    } finally {
      setCopyingRowId(null);
    }
  }

  return (
    <div className="space-y-4" data-testid="panel-city-ai-analyses">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold" data-testid="text-city-ai-analyses-title">
            City AI Analysis Cache
          </h3>
          <p className="text-sm text-muted-foreground">
            Pre-computed Gemini market analyses powering the Market Research
            page. Refreshed automatically on the 1st of each month at 00:00 UTC.
            Use Refresh now to re-run a single city, or Refresh all to re-run
            the entire batch on demand.
          </p>
          <a
            href="/app/admin/recommended-furnishings"
            className="inline-block mt-2 text-sm font-medium text-primary hover:underline"
            data-testid="link-recommended-furnishings"
          >
            → Recommended Furnishings (XLSX import/export)
          </a>
        </div>
        {canManage && (
        <Button
          size="sm"
          onClick={() => refreshAllMutation.mutate()}
          disabled={refreshAllMutation.isPending || isBatchRunning}
          data-testid="button-refresh-all-city-analyses"
        >
          {refreshAllMutation.isPending || isBatchRunning ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          {isBatchRunning ? "Refresh in progress…" : "Refresh all"}
        </Button>
        )}
      </div>

      {/* Task #1156 — Live progress indicator for the in-flight batch.
          The runner publishes `attempted` (total cities) and `completed`
          (refreshed + skipped + failed) on every poll, so we can render
          a real "X of N" without estimating. The poll cadence on the
          list query above tightens to 3s while a batch is running so
          this stays close to real time. */}
      {refreshStatus && refreshStatus.state === "running" ? (
        <Card
          className="p-4 border-blue-300 bg-blue-50 dark:bg-blue-950/30"
          data-testid="card-city-ai-analyses-refresh-progress"
        >
          <div className="flex items-start gap-3">
            <Loader2 className="h-5 w-5 text-blue-600 mt-0.5 shrink-0 animate-spin" />
            <div className="space-y-1 flex-1">
              <p
                className="text-sm font-semibold text-blue-900 dark:text-blue-200"
                data-testid="text-city-ai-analyses-refresh-progress"
              >
                Refresh in progress —{" "}
                {refreshStatus.completed} of {refreshStatus.attempted || "?"} cities done
                {refreshStatus.trigger === "cron" ? " (monthly cron)" : ""}
              </p>
              <p className="text-xs text-blue-800 dark:text-blue-300">
                {refreshStatus.lastCity
                  ? `Last finished: ${refreshStatus.lastCity}. `
                  : "Resolving city list… "}
                Refreshed {refreshStatus.refreshed}, skipped {refreshStatus.skipped}, failed {refreshStatus.failed} so far.
                Each Gemini call takes ~15-20s, so a full pass takes about 10 minutes.
              </p>
            </div>
          </div>
        </Card>
      ) : null}

      {/* Task #1149 — Banner that calls out a stuck monthly refresh
          before staff have to eyeball 36 rows. Only renders when the
          server-computed staleCount > 0. */}
      {staleCount > 0 ? (
        <Card
          className="p-4 border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30"
          data-testid="banner-city-ai-analyses-stale"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p
                className="text-sm font-semibold text-yellow-900 dark:text-yellow-200"
                data-testid="text-city-ai-analyses-stale-count"
              >
                {staleCount === 1
                  ? `1 city hasn't refreshed in over ${staleDays} days`
                  : `${staleCount} cities haven't refreshed in over ${staleDays} days`}
              </p>
              <p className="text-xs text-yellow-800 dark:text-yellow-300">
                The monthly refresh runs on the 1st of each month at 00:00
                UTC. A row older than {staleDays} days suggests the cron
                skipped these cities entirely. Click "Refresh now" on each
                stale row, or "Refresh all" to re-run the whole batch.
              </p>
            </div>
          </div>
        </Card>
      ) : null}

      {listQuery.isLoading ? (
        <Card className="p-6">
          <Skeleton className="h-32 w-full" />
        </Card>
      ) : listQuery.error ? (
        <Card className="p-6" data-testid="card-city-ai-analyses-error">
          <p className="text-sm text-destructive">
            Failed to load: {parseErrorMessage(listQuery.error as Error, "Unknown error")}
          </p>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="p-6" data-testid="card-city-ai-analyses-empty">
          <p className="text-sm text-muted-foreground">
            No cached analyses yet. The first deploy backfill or the monthly
            cron will populate this table.
          </p>
        </Card>
      ) : (
        <>
          {totals && totals.tracked > 0 ? (
            <Card
              className="p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm"
              data-testid="card-city-ai-analyses-totals"
            >
              <span className="font-medium">Most recent refresh totals</span>
              <span className="text-muted-foreground">
                {totals.tracked.toLocaleString()} of {rows.length.toLocaleString()} cities tracked
              </span>
              <span data-testid="text-totals-input-tokens">
                <span className="text-muted-foreground">Input tokens: </span>
                <span className="font-mono tabular-nums">
                  {totals.inputTokens.toLocaleString()}
                </span>
              </span>
              <span data-testid="text-totals-output-tokens">
                <span className="text-muted-foreground">Output tokens: </span>
                <span className="font-mono tabular-nums">
                  {totals.outputTokens.toLocaleString()}
                </span>
              </span>
              <span data-testid="text-totals-cost-usd">
                <span className="text-muted-foreground">Gemini spend: </span>
                <span className="font-mono tabular-nums font-semibold">
                  {formatUsd(totals.costUsd)}
                </span>
              </span>
            </Card>
          ) : null}
          <Card className="p-0" data-testid="card-city-ai-analyses-table">
            <Table data-testid="table-city-ai-analyses">
              <TableHeader>
                <TableRow>
                  <TableHead>City</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Last refreshed</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead className="text-right">Size</TableHead>
                  <TableHead className="text-right">Input tok</TableHead>
                  <TableHead className="text-right">Output tok</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead>Preview</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const key = `${row.city}|${row.state}`;
                  const isRefreshing = refreshingKey === key;
                  return (
                    <TableRow
                      key={row.id}
                      className={
                        row.isStale
                          ? "bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-950/30 dark:hover:bg-yellow-950/40"
                          : undefined
                      }
                      data-testid={`row-city-ai-analysis-${row.id}`}
                      data-stale={row.isStale ? "true" : "false"}
                    >
                      <TableCell
                        className="font-medium"
                        data-testid={`text-city-${row.id}`}
                      >
                        <div className="flex items-center gap-2">
                          {row.isStale ? (
                            <AlertTriangle
                              className="h-4 w-4 text-yellow-600 shrink-0"
                              aria-label="Stale — older than the refresh threshold"
                              data-testid={`icon-stale-${row.id}`}
                            />
                          ) : null}
                          <span>{row.city}</span>
                        </div>
                      </TableCell>
                      <TableCell data-testid={`text-state-${row.id}`}>{row.state}</TableCell>
                      <TableCell
                        className={
                          row.isStale
                            ? "text-sm text-yellow-800 dark:text-yellow-200 font-medium"
                            : "text-sm text-muted-foreground"
                        }
                        data-testid={`text-generated-at-${row.id}`}
                      >
                        {formatDate(row.generatedAt)}
                      </TableCell>
                      <TableCell
                        className="font-mono text-xs"
                        data-testid={`text-model-${row.id}`}
                      >
                        {row.model}
                      </TableCell>
                      <TableCell
                        className="text-right tabular-nums text-sm"
                        data-testid={`text-byte-size-${row.id}`}
                      >
                        {row.byteSize.toLocaleString()} B
                      </TableCell>
                      <TableCell
                        className="text-right tabular-nums text-sm"
                        data-testid={`text-input-tokens-${row.id}`}
                      >
                        {row.inputTokens != null ? row.inputTokens.toLocaleString() : "—"}
                      </TableCell>
                      <TableCell
                        className="text-right tabular-nums text-sm"
                        data-testid={`text-output-tokens-${row.id}`}
                      >
                        {row.outputTokens != null ? row.outputTokens.toLocaleString() : "—"}
                      </TableCell>
                      <TableCell
                        className="text-right tabular-nums text-sm"
                        data-testid={`text-cost-usd-${row.id}`}
                      >
                        {row.costUsd != null ? formatUsd(row.costUsd) : "—"}
                      </TableCell>
                      <TableCell
                        className="max-w-md text-xs text-muted-foreground"
                        data-testid={`text-snippet-${row.id}`}
                      >
                        <span className="line-clamp-2">{row.snippet}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* 2026-04-29 — Copy fetches the full markdown
                              for this row on demand and writes it to the
                              clipboard. Disabled only while THIS row's
                              copy is in flight so other rows stay
                              clickable. */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyMarkdownForRow(row)}
                            disabled={copyingRowId === row.id}
                            data-testid={`button-copy-city-${row.id}`}
                            title="Copy full analysis as markdown"
                          >
                            {copyingRowId === row.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                          {/* 2026-04-29 — Preview opens the dialog
                              defined at the bottom of this panel,
                              which lazy-loads the full markdown via
                              previewQuery and renders it scrollably. */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPreviewRow(row)}
                            data-testid={`button-preview-city-${row.id}`}
                            title="Preview full analysis"
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          {canManage && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              refreshOneMutation.mutate({ city: row.city, state: row.state })
                            }
                            disabled={
                              isRefreshing || refreshAllMutation.isPending || isBatchRunning
                            }
                            data-testid={`button-refresh-city-${row.id}`}
                          >
                            {isRefreshing ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3 w-3 mr-1" />
                            )}
                            Refresh now
                          </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      {/* 2026-04-29 — Preview dialog. Renders the full markdown for the
          currently-selected row, scrollable so admins can read the entire
          Gemini analysis without leaving the admin panel. The Copy
          button at the top reuses the cached previewQuery payload (no
          extra fetch). */}
      <Dialog
        open={previewRow !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewRow(null);
        }}
      >
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
          data-testid="dialog-city-ai-analysis-preview"
        >
          <DialogHeader>
            <DialogTitle data-testid="text-preview-city">
              {previewRow ? `${previewRow.city}, ${previewRow.state}` : ""}
            </DialogTitle>
            <DialogDescription>
              {previewRow
                ? `${previewRow.model} · refreshed ${formatDate(previewRow.generatedAt)} · ${previewRow.byteSize.toLocaleString()} bytes`
                : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-end gap-2 pb-2 border-b">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (previewRow && previewQuery.data) {
                  copyMarkdownForRow(previewRow, previewQuery.data.analysis);
                } else if (previewRow) {
                  copyMarkdownForRow(previewRow);
                }
              }}
              disabled={
                !previewRow ||
                copyingRowId === previewRow?.id ||
                previewQuery.isLoading
              }
              data-testid="button-copy-from-preview"
            >
              {previewRow && copyingRowId === previewRow.id ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Copy className="h-3 w-3 mr-1" />
              )}
              Copy markdown
            </Button>
          </div>

          <div
            className="flex-1 overflow-y-auto py-4"
            data-testid="container-preview-content"
          >
            {previewQuery.isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : previewQuery.error ? (
              <p className="text-sm text-destructive" data-testid="text-preview-error">
                Failed to load:{" "}
                {parseErrorMessage(previewQuery.error as Error, "Unknown error")}
              </p>
            ) : previewQuery.data ? (
              <article
                className="prose prose-sm dark:prose-invert max-w-none"
                data-testid="text-preview-markdown"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {previewQuery.data.analysis}
                </ReactMarkdown>
              </article>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
